/**
 * Inicio de jornada (BHM-CLD-REQ-042 §11-12) y telemetría funcional (§23-24).
 * - Recordatorio según el horario configurado del empleado (nunca una hora fija en código).
 * - Notificación del navegador/PWA cuando el usuario la ha permitido; sin servicios externos de pago.
 * - No ficha automáticamente, no avisa si ya ha fichado y no repite (1 aviso + 1 recordatorio al día).
 * - Telemetría: solo tipo de evento, módulo e identificadores; nunca texto, teclas ni portapapeles.
 */
import { mailApi } from '../../stores/mailApi';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { nexusApi, type Schedule } from '../../stores/nexusApi';
import { Icon } from './WorkUi';

const madridNow = () => {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', weekday: 'short', hour12: false }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const wd = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(get('weekday')) + 1;
  return { date: `${get('year')}-${get('month')}-${get('day')}`, hhmm: `${get('hour')}:${get('minute')}`, weekday: wd };
};
const minutes = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* sin almacenamiento: se avisará en pantalla */ } },
};

/** Decide si toca avisar (lógica pura, testeable). */
export function reminderDue(s: Schedule | null, now: { date: string; hhmm: string; weekday: number }, sent: { first: boolean; second: boolean }): 'FIRST' | 'SECOND' | null {
  if (!s?.workStartTime || s.clockedIn) return null;
  if (!s.workDays.includes(now.weekday)) return null;
  const late = minutes(now.hhmm) - minutes(s.workStartTime);
  if (late < 0 || late > 8 * 60) return null;
  if (!sent.first) return 'FIRST';
  if (!sent.second && late >= s.reminderAfterMin) return 'SECOND';
  return null;
}

async function showNotification(body: string) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker?.getRegistration?.();
    if (reg) await reg.showNotification('BarmetHub', { body, tag: 'bhm-start-of-day', icon: '/pwa-192x192.png', data: { url: '/nexus' } });
    else new Notification('BarmetHub', { body, tag: 'bhm-start-of-day' });
    return true;
  } catch { return false; }
}

/** Web Push (REQ-043 §35): suscribe este navegador para recibir el aviso de jornada aunque BarmetHub esté cerrado. */
export async function subscribePush(): Promise<'SUBSCRIBED' | 'DENIED' | 'UNSUPPORTED' | 'DISABLED'> {
  if (typeof Notification === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) return 'UNSUPPORTED';
  const st = await mailApi.pushStatus().catch(() => null);
  if (!st?.enabled || !st.publicKey) return 'DISABLED';
  const perm = Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission;
  if (perm !== 'granted') return 'DENIED';
  const reg = await navigator.serviceWorker.ready;
  const key = Uint8Array.from(atob(st.publicKey.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (st.publicKey.length % 4)) % 4)), (c) => c.charCodeAt(0));
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key }));
  const j = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await mailApi.pushSubscribe({ endpoint: j.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth });
  return 'SUBSCRIBED';
}

/** Aviso en Nexus: estado real del aviso de jornada en este dispositivo. */
export function PushOptIn() {
  const [state, setState] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<Awaited<ReturnType<typeof nexusApi.schedule>> | null>(null);
  useEffect(() => {
    void nexusApi.schedule().then(setSchedule).catch(() => setSchedule(null));
    void (async () => {
      const st = await mailApi.pushStatus().catch(() => null);
      if (!st?.enabled) return setState('DISABLED');
      if (typeof Notification === 'undefined' || !('PushManager' in window)) return setState('UNSUPPORTED');
      const reg = await navigator.serviceWorker?.getRegistration?.();
      const sub = await reg?.pushManager.getSubscription();
      setState(sub && Notification.permission === 'granted' ? 'SUBSCRIBED' : Notification.permission === 'denied' ? 'DENIED' : 'OFF');
    })();
  }, []);
  if (!schedule) return null;
  if (!schedule.workStartTime) return <p className="text-[11px] text-outline" data-testid="schedule-pending">Aviso de inicio de jornada: configuración pendiente (dirección debe fijar tu hora de entrada en Accesos empleados).</p>;
  const label = state === 'SUBSCRIBED' ? `Aviso de jornada activo en este dispositivo (${schedule.workStartTime}).` : state === 'DENIED' ? 'Has bloqueado las notificaciones en este navegador.' : state === 'UNSUPPORTED' ? 'Este navegador no admite avisos con la app cerrada.' : state === 'DISABLED' ? `Aviso de jornada a las ${schedule.workStartTime} (solo con BarmetHub abierto).` : null;
  return (
    <p className="flex flex-wrap items-center gap-2 text-[11px] text-outline" data-testid="push-optin">
      {label}
      {state === 'OFF' && <button className="rounded-lg border border-outline-variant px-2 py-0.5 text-on-surface-variant hover:text-on-surface" onClick={async () => setState(await subscribePush().catch(() => 'UNSUPPORTED'))}>Recibir el aviso de las {schedule.workStartTime} aunque BarmetHub esté cerrado</button>}
    </p>
  );
}

export function StartOfDayReminder({ enabled }: { enabled: boolean }) {
  const [banner, setBanner] = useState<string | null>(null);
  const [perm, setPerm] = useState(typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const check = async () => {
      try {
        const s = await nexusApi.schedule();
        const now = madridNow();
        if (s.clockedIn) { setBanner(null); return; }
        const k1 = `bhm-sod-${now.date}`; const k2 = `${k1}-2`;
        const due = reminderDue(s, now, { first: !!store.get(k1), second: !!store.get(k2) });
        if (!due || !alive) return;
        const msg = due === 'FIRST'
          ? 'Es hora de comenzar tu jornada. Entra en BarmetHub para fichar y revisar con Nexus tus prioridades.'
          : 'Todavía no has fichado la entrada. Ficha y revisa tus prioridades con Nexus.';
        store.set(due === 'FIRST' ? k1 : k2, '1');
        setBanner(msg);
        const shown = await showNotification(msg);
        void nexusApi.activity([{ type: 'NOTIFICATION_SHOWN', module: shown ? 'notification' : 'banner' }]).catch(() => undefined);
      } catch { /* sin horario o sin permisos: no se avisa */ }
    };
    void check();
    const t = setInterval(check, 5 * 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [enabled]);
  if (!banner) return null;
  return (
    <div className="mx-6 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#FF5E1E]/40 bg-[#FF5E1E]/10 px-4 py-3 text-sm" role="status" data-testid="start-of-day">
      <span className="flex items-center gap-2 text-on-surface"><Icon name="schedule" className="text-base text-[#FF5E1E]" />{banner}</span>
      <span className="flex gap-2">
        {perm === 'default' && <button className="rounded-lg border border-outline-variant px-3 py-1 text-xs" onClick={async () => { const r = await subscribePush().catch(() => 'UNSUPPORTED'); setPerm(r === 'SUBSCRIBED' ? 'granted' : typeof Notification === 'undefined' ? 'unsupported' : Notification.permission); }}>Activar notificaciones</button>}
        <Link to="/nexus" onClick={() => { setBanner(null); void nexusApi.activity([{ type: 'NOTIFICATION_OPENED', module: 'nexus' }]).catch(() => undefined); }} className="rounded-lg bg-[#FF5E1E] px-3 py-1 text-xs font-medium text-white">Abrir Nexus</Link>
        <button aria-label="Cerrar" className="text-on-surface-variant" onClick={() => setBanner(null)}><Icon name="close" className="text-base" /></button>
      </span>
    </div>
  );
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Evento estructurado por cambio de pantalla (sin parámetros de búsqueda ni contenido). */
export function activityForPath(pathname: string): { type: string; module?: string; projectId?: string }[] {
  const seg = pathname.split('/').filter(Boolean);
  const module = (seg[0] ?? 'dashboard').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40) || 'dashboard';
  const out: { type: string; module?: string; projectId?: string }[] = [{ type: 'MODULE_OPENED', module }];
  if (seg[0] === 'proyectos' && seg[1] && UUID.test(seg[1])) out.push({ type: 'PROJECT_OPENED', module: 'proyectos', projectId: seg[1] });
  return out;
}

export function ActivityTracker({ enabled }: { enabled: boolean }) {
  const { pathname } = useLocation();
  const last = useRef('');
  useEffect(() => {
    if (!enabled || pathname === last.current || pathname.startsWith('/login')) return;
    last.current = pathname;
    void nexusApi.activity(activityForPath(pathname)).catch(() => undefined);
  }, [enabled, pathname]);
  return null;
}
